import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useNotifier } from '@cbgms/base/notifier'
import { useTranslation } from 'react-i18next'
import { DeleteAlertMessage, DeleteAlertMessageResponse, FetchAlertMessage } from '../state/actions'
import { makeStyles } from '@mui/styles'
import { TooltipButton } from '@cbgms/components/form/TooltipButton'
import Alert from './AddEdit'
import moment from 'moment'
import { AlertStatus } from '../state/types'
import DeleteConfirmationPopup from './DeleteConfirmationPopup'
import { IRootReducerState } from 'state/reducers'

interface alertPopupType {
  Id: string
  Text: string
  CreatedFrom: string
  type?: string
}
interface Criterion {
  Name: string
  Value: string
  Comparison: string
}

interface CriteriaPopUp {
  CriteriaProp: Criterion[]
  type?: string
}

interface AlertMessage {
  AlertMessageUUID: string
  Name: string
  AppId: number
  Text: string
  ValidFrom: string
  ValidTo: string
  CreatedBy: string
  CreatedAt: string
  CriteriaUse: boolean
  Link: string
  Criteria: Array<{
    CriteriaId: number
    Name: string
    Value: string
    Comparison: string
  }>
  initialAlertData?: AlertMessage | null
}

export interface AlertMessageResponse {
  Data: AlertMessage[]
}
const useStyles = makeStyles({
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  moduleText: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  moduleTitle: {
    marginBottom: '2px',
    marginLeft: '18px',
    color: '#808080',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  backButton: {
    alignItems: 'flex-start'
  },
  saveButton: {
    marginBottom: '1px'
  },
  tableHeaderCell: {
    padding: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#808080',
    whiteSpace: 'nowrap'
  },
  tableHeaderCellCenter: {
    padding: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#808080',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  tableCell: {
    fontSize: 14,
    padding: '4px',
    color: '#808080'
  },
  tableCellCenter: {
    fontSize: 14,
    padding: '4px',
    color: '#808080',
    textAlign: 'center'
  },
  textField: {
    width: '100px'
  },
  tableRowOdd: {
    backgroundColor: 'action.hover'
  },
  tableRowLast: {
    border: 0
  },
  paperContainer: {
    backgroundColor: 'white',
    position: 'relative',
    marginTop: '5px',
    marginBottom: '5px'
  },
  tableContainer: {
    padding: '16px',
    backgroundColor: 'white',
    position: 'absolute',
    marginTop: '45px',
    marginBottom: '10px',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    height: '90%'
  },
  moduleManagementTitle: {
    marginBottom: '1rem',
    fontSize: 20,
    fontWeight: 700
  },
  arrowIcon: {
    marginRight: '0.5em'
  },
  tableRowOddStyle: {
    '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
    '&:last-child td, &:last-child th': { border: 0 },
    height: '50px'
  },
  tableCellWordBreak: {
    wordBreak: 'break-word'
  },
  truncatedText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100px',
    display: 'inline-block',
    verticalAlign: 'bottom',
    cursor: 'pointer'
  },
  displayContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '97%',
    position: 'absolute',
    marginTop: '5px',
    marginLeft: '15px'
  },
  displayLabel: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '10px'
  },
  popoverArrow: {
    position: 'absolute',
    top: '-10px',
    left: '42%',
    width: '0',
    height: '0',
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: '10px solid white'
  },
  popoverCard: {
    width: '450px'
  },
  popoverBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px'
  },
  popoverBoxItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 0'
  },
  popoverBoxKey: {
    textAlign: 'right',
    flex: 1,
    paddingRight: '8px',
    whiteSpace: 'nowrap'
  },
  popoverBoxValue: {
    flex: 3,
    textAlign: 'left',
    paddingLeft: '8px',
    borderLeft: '2px solid #808080'
  },
  tooltipButtonContainer: {
    display: 'inline-flex',
    gap: '0',
    margin: 0,
    padding: 0
  },
  popoverBoxValueEven: {
    backgroundColor: '#f5f5f5'
  },
  popoverBoxValueOdd: {
    backgroundColor: 'white'
  },

  tooltipButton: {
    padding: '10px',
    fontSize: '13px'
  },
  marginLeft5: {
    marginLeft: '5px'
  },
  underline: {
    textDecoration: 'underline'
  },
  customStyleEdit: {
    marginRight: '0!important',
    padding: '10px!important',
    borderTopRightRadius: '0!important',
    borderBottomRightRadius: '0!important',
    fontSize: '13px!important'
  },
  customStyleDelete: {
    marginLeft: '0!important',
    padding: '10px!important',
    borderTopLeftRadius: '0!important',
    borderBottomLeftRadius: '0!`',
    fontSize: '13px!important'
  }
})

const CustomerAlert: React.FC = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const tableRef = React.useRef<HTMLDivElement>(null)
  const [alertMessage, setAlertMessage] = useState<AlertMessage[]>([])
  const [checkboxState, setCheckboxState] = useState<{ active: boolean; inactive: boolean }>({
    active: true,
    inactive: false
  })
  const [status, setStatus] = useState<string>(AlertStatus.ACTIVE)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [tableHeight, setTableHeight] = useState<number>(window.innerHeight)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [popoverContent, setPopoverContent] = useState<CriteriaPopUp | alertPopupType | null>(null)

  const [showEditPopup, setShowEditPopup] = useState(false)
  const [showAddPopup, setShowAddPopup] = useState(false)
  const [initialAlertData, setInitialAlertData] = useState<AlertMessage | null>(null)
  const [selectedAlert, setSelectedAlert] = useState<AlertMessage | null>(null)

  const [showPopup, setShowPopup] = useState(false)
  const [deletedMessege, setDeletedMessege] = useState<boolean>(false)
  const [deletePopUpOpen, setDeletePopUpOpen] = useState<boolean>(false)
  const [deleteAlertMessageData, setDeleteAlertMessageData] = useState<{ alertId: string; alertName: string }>(
    null as unknown as { alertId: string; alertName: string }
  )
  const AppId = useSelector((state: IRootReducerState) => state.auth.user.AppId) || ''

  const handleDeleteClick = async (deleteAlertMessageData: { alertId: string; alertName: string }) => {
    setDeletePopUpOpen(true)
    setDeleteAlertMessageData(deleteAlertMessageData)
  }
  const handleEditClick = (alertMessageData: AlertMessage) => {
    setSelectedAlert(alertMessageData)
    toggleEditPopup()
  }
  const handleDelete = async (alertMessageUUID: string) => {
    try {
      const deletedMessageStatus: DeleteAlertMessageResponse = (await dispatch(
        DeleteAlertMessage(alertMessageUUID)
      )) as DeleteAlertMessageResponse
      if (deletedMessageStatus.Data.Status === AlertStatus.ALL) {
        setDeletedMessege(true)
        notifier.success(t(deletedMessageStatus?.Data?.Message))
        fetchAlertMessages(AppId, status)
        showPopup && setShowPopup(false)
      }
    } catch (error) {
      notifier.error(`${t('Alert.AlertDelete')}, ${error}`)
      setLoading(false)
      showPopup && setShowPopup(false)
    } finally {
      setLoading(false)
      setDeletePopUpOpen(false)
    }
  }

  const DeletePopupBox = (props: { DeleteAlertMessage: { alertId: string; alertName: string } }) => {
    return (
      <DeleteConfirmationPopup
        open={deletePopUpOpen}
        headerContent={t('Alert.DeleteItem')}
        deleteItemName={props.DeleteAlertMessage.alertName}
        paragrapghContent={t('Alert.DeleteAlert')}
        isLoading={false}
        onCancel={() => setDeletePopUpOpen(false)}
        onConfirm={() => handleDelete(props.DeleteAlertMessage.alertId)}
      />
    )
  }
  const handleCheckboxChange = (type: 'active' | 'inactive') => {
    setCheckboxState(prevState => {
      const newState = { ...prevState, [type]: !prevState[type] }
      if (newState.active && newState.inactive) {
        setStatus(AlertStatus.ALL)
      } else if (newState.active) {
        setStatus(AlertStatus.ACTIVE)
      } else if (newState.inactive) {
        setStatus(AlertStatus.INACTIVE)
      } else {
        setStatus(AlertStatus.NONE)
      }
      return newState
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>, content: CriteriaPopUp | alertPopupType) => {
    setAnchorEl(event.currentTarget)
    setPopoverContent(content)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const fetchAlertMessages = useCallback(
    async (AppId: string, status: string) => {
      setDeletedMessege(false)
      setLoading(true)
      setError(null)
      try {
        if (status !== AlertStatus.NONE) {
          const alertData: AlertMessageResponse = (await dispatch(FetchAlertMessage(AppId, status))) as AlertMessageResponse
          setAlertMessage(alertData.Data)
        } else {
          setAlertMessage([])
        }
        setLoading(false)
      } catch (error) {
        setError(t('Alert.error'))
        notifier.error(t('Alert.error'))
        setLoading(false)
      } finally {
        setLoading(false)
      }
    },
    [dispatch, deletedMessege]
  )

  useEffect(() => {
    fetchAlertMessages(AppId, status)
  }, [fetchAlertMessages, status, AppId])

  const handleResize = useCallback(() => {
    if (tableRef.current) {
      setTableHeight(window.innerHeight - tableRef.current.getBoundingClientRect().top)
    }
  }, [])

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  const toggleEditPopup = () => {
    setShowEditPopup(!showEditPopup)
  }

  const toggleAddPopup = () => {
    setShowAddPopup(!showAddPopup)
    if (!showAddPopup) {
      setInitialAlertData(null)
    }
  }

  return (
    <div>
      {deletePopUpOpen && <DeletePopupBox DeleteAlertMessage={deleteAlertMessageData} />}
      <Box data-testid='moduleText' className={classes.moduleText}>
        <Typography variant='h6' className={classes.moduleManagementTitle}>
          {t('Alert.Alerts')}
        </Typography>

        <Box>
          <Button variant='contained' className={`ui tiny primary ml-half ${classes.backButton}`} onClick={toggleAddPopup}>
            <i className='add icon white-icon' />
            {t('Alert.Newlisting')}
          </Button>

          <Link to='/'>
            <Button variant='outlined' color='inherit' className={`ui tiny ml-half ${classes.backButton}`}>
              <i aria-hidden='true' className={`arrow left icon ${classes.arrowIcon}`} />
              {t('module.Back')}
            </Button>
          </Link>
        </Box>
      </Box>

      <Paper ref={tableRef} className={classes.paperContainer} style={{ height: tableHeight }}>
        <div className={classes.displayContainer}>
          <div className={classes.flexContainer}>
            {t('Alert.Display')}:
            <label className={classes.displayLabel}>
              <input
                type='checkbox'
                name='currentEntry'
                checked={checkboxState.active}
                onChange={() => handleCheckboxChange(AlertStatus.ACTIVE)}
              />
              <span className={classes.marginLeft5}>{t('Alert.currentEntries')}</span>
            </label>
            <label className={classes.displayLabel}>
              <input
                type='checkbox'
                name='expiredNotices'
                checked={checkboxState.inactive}
                onChange={() => handleCheckboxChange(AlertStatus.INACTIVE)}
              />
              <span className={classes.marginLeft5}>{t('Alert.expiredEntries')}</span>
            </label>
          </div>
        </div>
        <Box className={classes.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color='error'>{error}</Typography>
          ) : (
            <TableContainer component={Box}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.Name')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.Location')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.Criteria')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.StartDate')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.EndDate')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.Text')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.info')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('Alert.Action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!alertMessage.length ? (
                    <TableRow>
                      <TableCell colSpan={8} className={classes.tableCellCenter}>
                        {t('Alert.No_Alerts_Found')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    alertMessage.map(alertMessageData => (
                      <TableRow key={alertMessageData.AlertMessageUUID} className={classes.tableRowOddStyle}>
                        <TableCell className={classes.tableCellCenter}>{alertMessageData.Name}</TableCell>
                        <TableCell className={classes.tableCellCenter}>1</TableCell>
                        <TableCell className={classes.tableCellCenter}>
                          <a
                            href='#'
                            onClick={e =>
                              handleClick(e, {
                                CriteriaProp: alertMessageData.Criteria,
                                type: 'Criteria'
                              })
                            }
                            className={classes.underline}
                          >
                            {alertMessageData.CriteriaUse ? t('Alert.Criteria') : ''}
                          </a>
                        </TableCell>
                        <TableCell className={classes.tableCellCenter}>
                          {moment(alertMessageData.ValidFrom, 'DD.MM.YYYY HH:ss').format('DD/MM/YYYY HH:ss')}
                        </TableCell>
                        <TableCell className={classes.tableCellCenter}>
                          {moment(alertMessageData.ValidTo, 'DD.MM.YYYY HH:ss').format('DD/MM/YYYY HH:ss')}
                        </TableCell>
                        <TableCell className={classes.tableCellCenter}>
                          {alertMessageData.Text.length > 30 ? `${alertMessageData.Text.substring(0, 30)} ...` : alertMessageData.Text}
                        </TableCell>
                        <TableCell className={classes.tableCellCenter}>
                          <a
                            href='#'
                            onClick={e =>
                              handleClick(e, {
                                Id: alertMessageData.AlertMessageUUID,
                                Text: alertMessageData.Text,
                                CreatedFrom: `${alertMessageData.CreatedBy} on ${moment(alertMessageData.CreatedAt).format(
                                  'DD.MM.YYYY hh:mm A'
                                )}`,
                                type: 'Info'
                              })
                            }
                            className={classes.underline}
                          >
                            {t('Alert.info')}
                          </a>
                        </TableCell>
                        <TableCell className={classes.tableCellCenter}>
                          <div className={classes.tooltipButtonContainer}>
                            <TooltipButton
                              title={t('global.edit')}
                              icon='pencil'
                              data-testid='editButton'
                              onClick={() => handleEditClick(alertMessageData)}
                              className={classes.customStyleEdit}
                            />
                            <TooltipButton
                              title={t('global.delete')}
                              icon='trash'
                              data-testid='deleteButton'
                              className={classes.customStyleDelete}
                              onClick={() => {
                                handleDeleteClick({ alertId: alertMessageData.AlertMessageUUID, alertName: alertMessageData.Name })
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>
      <Alert
        open={showAddPopup}
        togglePopup={toggleAddPopup}
        callBackFetch={fetchAlertMessages}
        initialAlertData={initialAlertData}
        status={status}
        appId={AppId}
        isEditMode={false}
      />
      {selectedAlert && (
        <Alert
          open={showEditPopup}
          togglePopup={toggleEditPopup}
          alertDataProp={selectedAlert}
          callBackFetch={fetchAlertMessages}
          status={status}
          appId={AppId}
          isEditMode
        />
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        PaperProps={{
          style: { overflow: 'visible', marginTop: '10px' }
        }}
      >
        <div className={classes.popoverArrow} />
        <Card className={classes.popoverCard}>
          <CardContent>
            <Typography variant='h6'>{popoverContent?.type === 'Criteria' ? t('Alert.Criteria') : t('Alert.learnMore')}</Typography>
            <Box className={classes.popoverBox}>
              {popoverContent ? (
                popoverContent.type === 'Criteria' ? (
                  (popoverContent as CriteriaPopUp).CriteriaProp?.length > 0 ? (
                    <>
                      <Box className={classes.popoverBoxItem}>
                        <div className={classes.popoverBoxKey}>{t('Alert.Name')}</div>
                        <Typography variant='body2' className={classes.popoverBoxValue}>
                          {(popoverContent as CriteriaPopUp).CriteriaProp?.map((criterion: Criterion) => criterion.Name).join(' | ')}
                        </Typography>
                      </Box>

                      <Box className={classes.popoverBoxItem}>
                        <div className={classes.popoverBoxKey}>{t('Alert.Value')}</div>
                        <Typography variant='body2' className={classes.popoverBoxValue}>
                          {(popoverContent as CriteriaPopUp).CriteriaProp?.map((criterion: Criterion) => criterion.Value).join(' | ')}
                        </Typography>
                      </Box>

                      <Box className={classes.popoverBoxItem}>
                        <div className={classes.popoverBoxKey}>{t('Alert.Comparison')}</div>
                        <Typography variant='body2' className={classes.popoverBoxValue}>
                          {(popoverContent as CriteriaPopUp).CriteriaProp?.map((criterion: Criterion) => criterion.Comparison).join(' | ')}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box className={classes.popoverBoxItem}>
                      <Typography variant='body2' className={classes.popoverBoxValue}>
                        {t('Alert.No_Data')}
                      </Typography>
                    </Box>
                  )
                ) : popoverContent.type === 'Info' ? (
                  Object.entries(popoverContent).map(([key, value], index) =>
                    key !== 'type' ? (
                      <Box key={key} className={classes.popoverBoxItem}>
                        <div className={classes.popoverBoxKey}>{key} </div>
                        <Typography
                          variant='body2'
                          className={`${classes.popoverBoxValue} ${
                            index % 2 === 0 ? classes.popoverBoxValueEven : classes.popoverBoxValueOdd
                          }`}
                        >
                          {value}
                        </Typography>
                      </Box>
                    ) : null
                  )
                ) : null
              ) : (
                <Box className={classes.popoverBoxItem}>
                  <Typography variant='body2' className={classes.popoverBoxValue}>
                    {t('Alert.No_Data')}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Popover>
    </div>
  )
}

export default CustomerAlert
